import { type TextareaHTMLAttributes, type Ref, useId } from 'react';
import { cn } from '../../lib/utils';
import { fieldClass } from './fieldClass';
import { FieldError } from './FieldError';
import { FIELD_LABEL_TYPOGRAPHY } from './FieldLabel';

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  /**
   * `default` - standard bordered textarea with padding and focus ring.
   * `ghost`   - borderless, transparent background, no padding, no focus ring.
   *             Used for in-place editable text in inline edit rows where the
   *             textarea must visually match the read-mode value it replaces.
   */
  variant?: 'default' | 'ghost';
}

export function Textarea({ ref, className, label, error, id, variant = 'default', ...props }: TextareaProps & { ref?: Ref<HTMLTextAreaElement> }) {
  const isGhost = variant === 'ghost';
  const autoId = useId();
  const errorId = `${id ?? autoId}-error`;
  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <label htmlFor={id} className={`${FIELD_LABEL_TYPOGRAPHY} text-secondary`}>
          {label}
        </label>
      )}
      <textarea
        ref={ref}
        id={id}
        className={cn(
          'w-full text-sm text-secondary',
          'placeholder:text-text-light',
          'disabled:cursor-not-allowed disabled:opacity-50',
          isGhost
            ? 'resize-none border-0 bg-transparent p-0 focus:outline-none focus:ring-0'
            : cn(
                fieldClass({ error: Boolean(error) }),
                'resize-y px-3 py-2',
              ),
          className,
        )}
        {...props}
        aria-invalid={error ? true : props['aria-invalid']}
        aria-describedby={error ? [errorId, props['aria-describedby']].filter(Boolean).join(' ') : props['aria-describedby']}
      />
      <FieldError id={errorId} error={error} />
    </div>
  );
}
